import { Box, Text, Progress, Flex, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import { useUploadContext } from 'packages/dataPipeline/hooks/UploadContext';
import { usePipelineProgress } from 'packages/dataPipeline/hooks/useNodeProgress';
import { getElapsedTime } from 'packages/dataPipeline/ui/utils/getTimeElapsed';

const getTimeString = (date: Date) =>
  date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  });

export const DrawerProgressBar = () => {
  const { completeRecord } = useUploadContext();
  const { completedNodes, totalNodes } = usePipelineProgress();
  const borderColor = useColorModeValue('gray.100', 'gray.800');
  if (!completeRecord) {
    // pipeline instance is not available yet.
    return null;
  }

  const { startedAt, updatedAt } = completeRecord.meshRunRecord;

  const startedAtStr = getTimeString(startedAt);
  const lastUpdatedStr = getTimeString(updatedAt);

  const progressPercentage = (completedNodes / totalNodes) * 100;
  const allComplete = completedNodes === totalNodes;

  const timeElapsed = getElapsedTime({
    startedAt,
    endedAt: allComplete ? updatedAt : new Date(),
  });

  return (
    <Box
      p={4}
      border="1px"
      borderColor={borderColor}
      borderRadius="md"
      w="100%"
    >
      {/* Time Elapsed and Step Progress */}
      <Flex justify="space-between" align="center">
        <Text fontSize="sm" fontWeight="bold">
          Time elapsed: {timeElapsed}
        </Text>

        <Text fontSize="sm">
          Step {completedNodes} of {totalNodes} completed
        </Text>
      </Flex>

      <Flex justify="space-between" align="center">
        {/* Started Date */}
        <Text fontSize="xs" mt={2} color="gray.500">
          Started {startedAtStr}
        </Text>

        {/* Completed Date */}
        {allComplete && (
          <Text fontSize="xs" mt={2} color="gray.500">
            Completed {lastUpdatedStr}
          </Text>
        )}
      </Flex>

      {/* Progress Bar if not complete */}
      {!allComplete && (
        <Progress
          mt={2}
          value={progressPercentage}
          size="sm"
          colorScheme="green"
        />
      )}
    </Box>
  );
};
